import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Grid,
  Dialog,
  DialogContent,
  Typography,
  Chip,
  CircularProgress,
  Pagination,
} from "@mui/material";
import axios from "axios";

interface PhotoTag {
  tag_id: number;
  username: string;
  type: string;
}

interface Photo {
  photo_id: number;
  photo_url: string;
  uploaded_by: number;
  context: string;
  context_id: number;
  created_at: string;
  tags: PhotoTag[];
}

interface PhotoLibraryProps {
  username: string;
}

const PhotoLibrary: React.FC<PhotoLibraryProps> = ({ username }) => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const photosPerPage = 6; // 2 rows of 3 photos

  // Calculate pagination
  const totalPages = Math.ceil(photos.length / photosPerPage);
  const paginatedPhotos = photos.slice(
    (page - 1) * photosPerPage,
    page * photosPerPage
  );

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  useEffect(() => {
    if (!username) {
      setLoading(false);
      return;
    }
    fetchPhotos();
  }, [username]);

  const fetchPhotos = async () => {
    if (!username) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/photos/tagged/${username}`,
        {
          withCredentials: true,
          headers: {
            Accept: "application/json",
          },
        }
      );
      setPhotos(response.data);
    } catch (error) {
      console.error("Error fetching tagged photos:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveTag = async (tagId: number) => {
    try {
      await axios.delete(
        `${import.meta.env.VITE_API_URL}/photos/tags/${tagId}`,
        {
          withCredentials: true,
        }
      );
      fetchPhotos(); // Refresh photos after removing tag
    } catch (error) {
      console.error("Error removing tag:", error);
    }
  };

  const renderPhotoGridItem = (photo: Photo) => (
    <Grid item xs={4} sm={4} md={4} key={photo.photo_id}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          paddingTop: "100%", // 1:1 Aspect ratio
          cursor: "pointer",
          "&:hover .photo-overlay": {
            opacity: 1,
          },
        }}
        onClick={() => setSelectedPhoto(photo.photo_url)}
      >
        <Box
          component="img"
          src={photo.photo_url}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: 1,
          }}
        />
        {/* Tags overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            padding: 1,
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)",
            color: "white",
            fontSize: "0.8rem",
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
          }}
        >
          {photo.tags.map((tag) => (
            <Chip
              key={tag.tag_id}
              label={tag.username}
              size="small"
              onDelete={
                tag.username === username
                  ? () => handleRemoveTag(tag.tag_id)
                  : undefined
              }
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                color: "white",
                "& .MuiChip-label": {
                  fontSize: "0.7rem",
                },
                "& .MuiChip-deleteIcon": {
                  color: "white",
                  fontSize: "0.7rem",
                },
              }}
            />
          ))}
        </Box>
      </Box>
    </Grid>
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Tagged Photos
      </Typography>
      <Grid container spacing={2}>
        {paginatedPhotos.map(renderPhotoGridItem)}
      </Grid>

      {/* Add pagination */}
      {photos.length > photosPerPage && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      {/* Photo Modal */}
      <Dialog
        open={Boolean(selectedPhoto)}
        onClose={() => setSelectedPhoto(null)}
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
            overflow: "hidden",
          },
        }}
      >
        <DialogContent
          sx={{
            padding: 0,
            backgroundColor: "transparent",
            "&:first-of-type": {
              paddingTop: 0,
            },
          }}
        >
          {selectedPhoto && (
            <Box
              component="img"
              src={selectedPhoto}
              sx={{
                display: "block",
                maxWidth: "90vw",
                maxHeight: "90vh",
                width: "auto",
                height: "auto",
                objectFit: "contain",
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPhoto(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default PhotoLibrary;
